import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Settings, Plus, Sparkles, TrendingUp, Brain, 
  Moon, Heart, RefreshCw, Shield, Zap, Flame,
  CloudRain, AlertTriangle, Coffee, Activity
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Pre-defined symptoms by category
export const SYMPTOM_LIBRARY = {
  anxiety: [
    { key: 'anxiety_level', label: 'Anxiety Level', icon: Zap },
    { key: 'worry_intensity', label: 'Worry Intensity', icon: Brain },
    { key: 'physical_tension', label: 'Physical Tension', icon: Activity },
    { key: 'panic_symptoms', label: 'Panic Symptoms', icon: AlertTriangle },
    { key: 'avoidance_urges', label: 'Avoidance Urges', icon: Shield },
  ],
  depression: [
    { key: 'mood_level', label: 'Mood Level', icon: Heart },
    { key: 'energy_level', label: 'Energy Level', icon: Flame },
    { key: 'motivation', label: 'Motivation', icon: TrendingUp },
    { key: 'hopelessness', label: 'Hopelessness', icon: CloudRain },
    { key: 'self_criticism', label: 'Self-Criticism', icon: Brain },
    { key: 'anhedonia', label: 'Ability to Feel Pleasure', icon: Sparkles },
  ],
  ocd: [
    { key: 'intrusive_thoughts', label: 'Intrusive Thoughts', icon: Brain },
    { key: 'compulsion_urges', label: 'Compulsion Urges', icon: RefreshCw },
    { key: 'uncertainty_tolerance', label: 'Uncertainty Tolerance', icon: AlertTriangle },
    { key: 'distress_level', label: 'Distress Level', icon: Zap },
  ],
  trauma: [
    { key: 'flashback_intensity', label: 'Flashback Intensity', icon: AlertTriangle },
    { key: 'hypervigilance', label: 'Hypervigilance', icon: Shield },
    { key: 'emotional_numbing', label: 'Emotional Numbing', icon: Heart },
    { key: 'safety_feeling', label: 'Sense of Safety', icon: Shield },
    { key: 'grounding_level', label: 'Groundedness', icon: Activity },
  ],
  sleep: [
    { key: 'sleep_quality', label: 'Sleep Quality', icon: Moon },
    { key: 'sleep_anxiety', label: 'Sleep-Related Anxiety', icon: Zap },
    { key: 'energy_morning', label: 'Morning Energy', icon: Coffee },
    { key: 'restfulness', label: 'Restfulness', icon: Moon },
  ],
  eating: [
    { key: 'body_image', label: 'Body Image Distress', icon: Heart },
    { key: 'eating_urges', label: 'Problematic Eating Urges', icon: AlertTriangle },
    { key: 'fullness_awareness', label: 'Fullness Awareness', icon: Activity },
    { key: 'food_anxiety', label: 'Food-Related Anxiety', icon: Zap },
  ],
  addiction: [
    { key: 'craving_intensity', label: 'Craving Intensity', icon: Flame },
    { key: 'urge_to_use', label: 'Urge to Use/Act', icon: AlertTriangle },
    { key: 'trigger_reactivity', label: 'Trigger Reactivity', icon: Zap },
    { key: 'self_efficacy', label: 'Belief in Self-Control', icon: Shield },
  ],
  general: [
    { key: 'overall_wellbeing', label: 'Overall Wellbeing', icon: Sparkles },
    { key: 'stress_level', label: 'Stress Level', icon: Activity },
    { key: 'self_compassion', label: 'Self-Compassion', icon: Heart },
    { key: 'present_moment', label: 'Present Moment Awareness', icon: Brain },
    { key: 'life_satisfaction', label: 'Life Satisfaction', icon: TrendingUp },
  ],
  selfharm: [
    { key: 'urge_to_harm', label: 'Urge to Self-Harm', icon: AlertTriangle },
    { key: 'emotional_pain', label: 'Emotional Pain Level', icon: Heart },
    { key: 'coping_confidence', label: 'Confidence in Coping', icon: Shield },
  ]
};

export const CATEGORY_LABELS: Record<string, string> = {
  anxiety: 'Anxiety & Panic',
  depression: 'Depression & Low Mood',
  ocd: 'OCD & Intrusive Thoughts',
  trauma: 'Trauma & PTSD',
  sleep: 'Sleep Issues',
  eating: 'Eating Concerns',
  addiction: 'Addiction & Cravings',
  general: 'General Wellbeing',
  selfharm: 'Self-Harm Urges'
};

interface SymptomRating {
  key: string;
  label: string;
  value: number;
}

interface UniversalSymptomTrackerProps {
  onRatingsChange: (ratings: Record<string, number>) => void;
  mode: 'before' | 'after';
  previousRatings?: Record<string, number>;
  compact?: boolean;
}

export const UniversalSymptomTracker: React.FC<UniversalSymptomTrackerProps> = ({
  onRatingsChange,
  mode,
  previousRatings = {},
  compact = false
}) => {
  const [userSymptoms, setUserSymptoms] = useState<Array<{ key: string; label: string }>>([]);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserSymptoms();
  }, []);

  useEffect(() => {
    onRatingsChange(ratings);
  }, [ratings]);

  const loadUserSymptoms = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Default symptoms for non-logged in users
        setUserSymptoms([
          { key: 'mood_level', label: 'Mood Level' },
          { key: 'anxiety_level', label: 'Anxiety Level' },
          { key: 'stress_level', label: 'Stress Level' }
        ]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_symptom_tracking_preferences')
        .select('symptom_key, symptom_label')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (error) throw error;

      if (data && data.length > 0) {
        setUserSymptoms(data.map(d => ({ key: d.symptom_key, label: d.symptom_label })));
      } else {
        // Set defaults and show config on first use
        const defaults = [
          { key: 'mood_level', label: 'Mood Level' },
          { key: 'anxiety_level', label: 'Anxiety Level' },
          { key: 'stress_level', label: 'Stress Level' }
        ];
        setUserSymptoms(defaults);
        if (mode === 'before') setIsConfiguring(true);
      }
    } catch (error) {
      // Silent fail for loading symptoms
    } finally {
      setLoading(false);
    }
  };

  const updateRating = (key: string, value: number) => {
    setRatings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return <div className="animate-pulse h-32 bg-muted rounded-lg" />;
  }

  if (isConfiguring) {
    return (
      <SymptomConfigurator 
        currentSymptoms={userSymptoms}
        onSave={(symptoms) => {
          setUserSymptoms(symptoms);
          setIsConfiguring(false);
        }}
        onCancel={() => setIsConfiguring(false)}
      />
    );
  }

  return (
    <Card className={compact ? 'border-dashed' : ''}>
      <CardHeader className={compact ? 'pb-2' : ''}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {mode === 'before' ? 'How are you feeling right now?' : 'How do you feel after?'}
            </CardTitle>
            {!compact && (
              <CardDescription className="text-xs">
                Rate each area on a scale of 1-10
              </CardDescription>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setIsConfiguring(true)}
            title="Customise what you track"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {userSymptoms.map((symptom) => {
          const prevValue = previousRatings[symptom.key];
          const currentValue = ratings[symptom.key] || 5;
          const improvement = prevValue !== undefined ? currentValue - prevValue : null;
          
          return (
            <div key={symptom.key} className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">{symptom.label}</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{currentValue}/10</span>
                  {mode === 'after' && improvement !== null && improvement !== 0 && (
                    <Badge 
                      variant={improvement > 0 ? 'default' : 'secondary'}
                      className={`text-xs ${improvement > 0 ? 'bg-green-500' : 'bg-amber-500'}`}
                    >
                      {improvement > 0 ? '+' : ''}{improvement}
                    </Badge>
                  )}
                </div>
              </div>
              <Slider
                value={[currentValue]}
                onValueChange={([v]) => updateRating(symptom.key, v)}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
              {!compact && (
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{getScaleLabel(symptom.key, 'low')}</span>
                  <span>{getScaleLabel(symptom.key, 'high')}</span>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// Helper to get contextual scale labels
function getScaleLabel(key: string, end: 'low' | 'high'): string {
  const labels: Record<string, { low: string; high: string }> = {
    mood_level: { low: 'Very low', high: 'Excellent' },
    anxiety_level: { low: 'None', high: 'Severe' },
    stress_level: { low: 'Calm', high: 'Overwhelmed' },
    energy_level: { low: 'Exhausted', high: 'Energised' },
    motivation: { low: 'None', high: 'Highly motivated' },
    hopelessness: { low: 'Hopeful', high: 'Very hopeless' },
    craving_intensity: { low: 'None', high: 'Intense' },
    urge_to_harm: { low: 'None', high: 'Strong urge' },
    sleep_quality: { low: 'Very poor', high: 'Excellent' },
    intrusive_thoughts: { low: 'None', high: 'Constant' },
    compulsion_urges: { low: 'None', high: 'Overwhelming' },
    flashback_intensity: { low: 'None', high: 'Severe' },
    safety_feeling: { low: 'Unsafe', high: 'Very safe' },
    self_compassion: { low: 'Very critical', high: 'Very kind' },
    overall_wellbeing: { low: 'Very poor', high: 'Thriving' },
  };
  return labels[key]?.[end] || (end === 'low' ? '1' : '10');
}

// Symptom Configurator Component
interface SymptomConfiguratorProps {
  currentSymptoms: Array<{ key: string; label: string }>;
  onSave: (symptoms: Array<{ key: string; label: string }>) => void;
  onCancel: () => void;
}

const SymptomConfigurator: React.FC<SymptomConfiguratorProps> = ({
  currentSymptoms,
  onSave,
  onCancel
}) => {
  const [selectedSymptoms, setSelectedSymptoms] = useState<Set<string>>(
    new Set(currentSymptoms.map(s => s.key))
  );
  const [customSymptom, setCustomSymptom] = useState('');
  const [customSymptoms, setCustomSymptoms] = useState<Array<{ key: string; label: string }>>([]);
  const [saving, setSaving] = useState(false);

  const toggleSymptom = (key: string) => {
    const newSet = new Set(selectedSymptoms);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setSelectedSymptoms(newSet);
  };

  const addCustomSymptom = () => {
    if (!customSymptom.trim()) return;
    const key = `custom_${Date.now()}`;
    setCustomSymptoms([...customSymptoms, { key, label: customSymptom }]);
    setSelectedSymptoms(new Set([...selectedSymptoms, key]));
    setCustomSymptom('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Just use local state for non-logged in users
        const allSymptoms = [
          ...Object.values(SYMPTOM_LIBRARY).flat(),
          ...customSymptoms
        ];
        const selected = allSymptoms.filter(s => selectedSymptoms.has(s.key));
        onSave(selected.map(s => ({ key: s.key, label: s.label })));
        return;
      }

      // Clear existing and save new
      await supabase
        .from('user_symptom_tracking_preferences')
        .delete()
        .eq('user_id', user.id);

      const allSymptoms = [
        ...Object.values(SYMPTOM_LIBRARY).flat(),
        ...customSymptoms
      ];
      const toSave = allSymptoms
        .filter(s => selectedSymptoms.has(s.key))
        .map(s => ({
          user_id: user.id,
          symptom_key: s.key,
          symptom_label: s.label,
          category: getSymptomCategory(s.key),
          is_active: true
        }));

      if (toSave.length > 0) {
        const { error } = await supabase
          .from('user_symptom_tracking_preferences')
          .insert(toSave);
        if (error) throw error;
      }

      toast.success('Tracking preferences saved!');
      onSave(toSave.map(s => ({ key: s.symptom_key, label: s.symptom_label })));
    } catch (error) {
      toast.error('Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-primary/30">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Customise Your Tracking
        </CardTitle>
        <CardDescription>
          Select what you want to track before and after exercises. This personalises your experience and powers meaningful insights.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {Object.entries(SYMPTOM_LIBRARY).map(([category, symptoms]) => (
          <div key={category} className="space-y-2.5">
            <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground">
              {CATEGORY_LABELS[category]}
              {category === 'selfharm' && (
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 border-accent bg-accent/10 text-accent font-medium">
                  Sensitive
                </Badge>
              )}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {symptoms.map((symptom) => (
                <label 
                  key={symptom.key}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedSymptoms.has(symptom.key)
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border/60 hover:border-primary/40 bg-background/50'
                  }`}
                >
                  <Checkbox
                    checked={selectedSymptoms.has(symptom.key)}
                    onCheckedChange={() => toggleSymptom(symptom.key)}
                    className="shrink-0"
                  />
                  <span className="text-sm text-foreground leading-tight">{symptom.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {/* Custom symptoms */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Add Custom Tracking</h4>
          <div className="flex gap-2">
            <Input
              placeholder="e.g., Perfectionism, Social anxiety..."
              value={customSymptom}
              onChange={(e) => setCustomSymptom(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomSymptom()}
            />
            <Button onClick={addCustomSymptom} size="icon">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {customSymptoms.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {customSymptoms.map((s) => (
                <Badge key={s.key} variant="secondary" className="gap-1">
                  {s.label}
                  <button 
                    onClick={() => {
                      setCustomSymptoms(customSymptoms.filter(c => c.key !== s.key));
                      const newSet = new Set(selectedSymptoms);
                      newSet.delete(s.key);
                      setSelectedSymptoms(newSet);
                    }}
                    className="ml-1 hover:text-destructive"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm text-foreground/90">
          <p className="font-medium text-foreground mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-secondary" />
            How this helps you:
          </p>
          <ul className="space-y-1.5 text-foreground/80">
            <li>• Track changes before/after every exercise</li>
            <li>• See correlations: "Gratitude journalling → 23% mood improvement"</li>
            <li>• Identify which exercises work best for YOUR specific challenges</li>
            <li>• Build evidence that your efforts are making a difference</li>
          </ul>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || selectedSymptoms.size === 0}
            className="flex-1"
          >
            {saving ? 'Saving...' : `Save (${selectedSymptoms.size} selected)`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

function getSymptomCategory(key: string): string {
  for (const [category, symptoms] of Object.entries(SYMPTOM_LIBRARY)) {
    if (symptoms.some(s => s.key === key)) return category;
  }
  return 'custom';
}

export default UniversalSymptomTracker;
